import { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { useAppStore } from '@/store/appStore'
import { supabase } from '@/lib/supabase'
import { AddStockModal } from '@/components/watchlist/AddStockModal'
import { computeCompression, computeTechnicalStatus, computeIdealEntryZone } from '@/lib/watchlistComputations'
import { fetchSMABatch, isSMAFetching } from '@/lib/twelveDataClient'
import type { SMAData } from '@/lib/watchlistComputations'
import { Tooltip, VALUATION_TOOLTIPS } from '@/components/shared/Tooltip'
import { invokeEdgeFunction } from '@/lib/edgeFunctions'

// Column ids that carry a valuation-metric tooltip on their header.
const VALUATION_COLUMNS = new Set(['forward_pe', 'peg_ratio', 'price_sales'])

/** Color class for a valuation metric value per the user's green/amber/red thresholds. */
function valuationColorClass(colId: string, val: number): string {
  if (colId === 'forward_pe') {
    if (val < 20) return 'text-green-600 font-semibold'
    if (val > 30) return 'text-red-600 font-semibold'
    return 'text-amber-600 font-medium'
  }
  if (colId === 'peg_ratio') {
    if (val < 1.0) return 'text-green-600 font-semibold'
    if (val > 2.0) return 'text-red-600 font-semibold'
    return 'text-amber-600 font-medium'
  }
  if (colId === 'price_sales') {
    if (val < 13) return 'text-green-600 font-semibold'
    if (val >= 20) return 'text-red-600 font-semibold'
    return 'text-amber-600 font-medium'
  }
  return 'text-gray-700'
}

// Columns that are manually editable (static values)
const EDITABLE_COLUMNS = new Set([
  'best_strategy', 'ownership_desire', 'wheel_quality', 'leap_call_quality',
  'assignment_comfort', 'volatility', 'earnings_risk', 'own_score_3y',
  'investment_thesis', 'ai_stack_role', 'priority_tier', 'sub_category', 'category',
])

// ========== Constants ==========

const ALL_COLUMNS = [
  { id: 'row_number', label: '#', width: 35 },
  { id: 'ticker', label: 'Ticker', width: 75 },
  { id: 'company_name', label: 'Name', width: 190 },
  { id: 'category', label: 'Category', width: 220 },
  { id: 'sub_category', label: 'Sub-Category', width: 200 },
  { id: 'priority_tier', label: 'Tier', width: 65 },
  { id: 'price_current', label: 'Current', width: 85 },
  { id: 'high_52w', label: '52 High', width: 85 },
  { id: 'high_6m', label: '6M High', width: 85 },
  { id: 'discount_6m', label: '6M Discount', width: 100 },
  { id: 'forward_pe', label: 'Fwd P/E', width: 85 },
  { id: 'peg_ratio', label: 'PEG', width: 70 },
  { id: 'price_sales', label: 'P/S', width: 70 },
  { id: 'technical_status', label: 'Tech Status', width: 100 },
  { id: 'ideal_entry_zone', label: 'Entry Zone', width: 140 },
  { id: 'best_strategy', label: 'Best Strategy', width: 200 },
  { id: 'ownership_desire', label: 'Ownership', width: 130 },
  { id: 'wheel_quality', label: 'Wheel', width: 100 },
  { id: 'leap_call_quality', label: 'LEAP Call', width: 100 },
  { id: 'assignment_comfort', label: 'Comfort', width: 110 },
  { id: 'volatility', label: 'Volatility', width: 90 },
  { id: 'earnings_risk', label: 'Earnings', width: 85 },
  { id: 'own_score_3y', label: 'Score', width: 55 },
  { id: 'investment_thesis', label: 'Thesis', width: 220 },
  { id: 'ai_stack_role', label: 'AI Stack Role', width: 300 },
] as const

type ColumnId = typeof ALL_COLUMNS[number]['id']

const USER_ID = '00000000-0000-0000-0000-000000000000'

// ========== Supabase Preferences Persistence ==========

interface WatchlistPrefs {
  column_order: string[]
  column_widths: Record<string, number>
  hidden_columns: string[]
  sort_column: string
  sort_direction: 'asc' | 'desc'
}

const DEFAULT_PREFS: WatchlistPrefs = {
  column_order: ALL_COLUMNS.map(c => c.id),
  column_widths: Object.fromEntries(ALL_COLUMNS.map(c => [c.id, c.width])),
  hidden_columns: [],
  sort_column: 'discount_6m',
  sort_direction: 'desc',
}

async function loadPrefsFromSupabase(): Promise<WatchlistPrefs> {
  try {
    const { data, error } = await supabase
      .from('watchlist_preferences')
      .select('*')
      .eq('user_id', USER_ID)
      .single()
    if (error || !data) return DEFAULT_PREFS
    // Merge stored order with ALL_COLUMNS: keep only ids that still exist, then append
    // any new columns (e.g. forward_pe/peg_ratio/price_sales) that were added after the
    // pref row was last saved — otherwise brand-new columns would never render.
    const allIds: string[] = ALL_COLUMNS.map(c => c.id)
    const validIds = new Set(allIds)
    const storedOrder: string[] = Array.isArray(data.column_order) ? data.column_order : DEFAULT_PREFS.column_order
    const keptStored = storedOrder.filter(id => validIds.has(id))
    const missing = allIds.filter(id => !keptStored.includes(id))
    const mergedOrder = [...keptStored, ...missing]
    return {
      column_order: mergedOrder,
      column_widths: data.column_widths || DEFAULT_PREFS.column_widths,
      hidden_columns: data.hidden_columns || DEFAULT_PREFS.hidden_columns,
      sort_column: data.sort_column || DEFAULT_PREFS.sort_column,
      sort_direction: data.sort_direction || DEFAULT_PREFS.sort_direction,
    }
  } catch { return DEFAULT_PREFS }
}

// Debounced save to Supabase (avoids hammering DB during rapid resize/reorder)
let saveTimer: ReturnType<typeof setTimeout> | null = null
function savePrefsToSupabase(prefs: Partial<WatchlistPrefs>) {
  if (saveTimer) clearTimeout(saveTimer)
  saveTimer = setTimeout(async () => {
    await supabase
      .from('watchlist_preferences')
      .upsert({ user_id: USER_ID, ...prefs, updated_at: new Date().toISOString() }, { onConflict: 'user_id' })
  }, 500)
}

// ========== Main Component ==========

export function WatchlistPage() {
  // State
  const [rows, setRows] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddModal, setShowAddModal] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  // SMA state with progress tracking
  const [smaData, setSmaData] = useState<Record<string, SMAData>>({})
  const [smaLoading, setSmaLoading] = useState(false)
  const [smaProgress, setSmaProgress] = useState({ done: 0, total: 0 })
  const [smaConfirm, setSmaConfirm] = useState(false)

  // SMA fetch handler — called after user picks "All" or "Missing"
  const startSmaFetch = (mode: 'all' | 'missing') => {
    const allTickers = rows.map(r => r.ticker).filter(Boolean)
    const missingTickers = rows.filter(r => !r.technical_status).map(r => r.ticker).filter(Boolean)
    const tickersToFetch = mode === 'all' ? allTickers : missingTickers

    if (tickersToFetch.length === 0) {
      alert('No tickers to fetch.')
      setSmaConfirm(false)
      return
    }

    setSmaConfirm(false)
    setSmaLoading(true)
    setSmaProgress({ done: 0, total: tickersToFetch.length })
    let doneCount = 0
    fetchSMABatch(tickersToFetch, (t, d) => {
      doneCount++
      setSmaProgress({ done: doneCount, total: tickersToFetch.length })
      if (d) {
        setSmaData(p => ({...p, [t]: d}))
        // Persist Tech Status + Entry Zone to Supabase
        const row = rows.find(r => r.ticker === t)
        if (row) {
          const currentPrice = Number(row.price_current) || 0
          if (currentPrice > 0 && d.sma_50) {
            const techStatus = computeTechnicalStatus(currentPrice, d.sma_50)
            const entryZone = computeIdealEntryZone(currentPrice, d.sma_20, d.sma_50, d.sma_200)
            ;(async () => {
              await supabase.from('watchlist').update({
                ...(techStatus ? { technical_status: techStatus } : {}),
                ...(entryZone ? { ideal_entry_zone: entryZone } : {}),
              }).eq('id', row.id)
            })()
          }
        }
      }
    })
    const c = setInterval(() => {
      if (!isSMAFetching()) { setSmaLoading(false); clearInterval(c) }
    }, 2000)
  }
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

  // Column order (persistable to Supabase)
  const [columnOrder, setColumnOrder] = useState<string[]>(DEFAULT_PREFS.column_order)
  // Column widths (persistable to Supabase)
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(DEFAULT_PREFS.column_widths)
  // Sort (persistable to Supabase)
  const [sortCol, setSortCol] = useState<string>(DEFAULT_PREFS.sort_column)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(DEFAULT_PREFS.sort_direction)
  // Hidden columns (persistable to Supabase)
  const [hiddenColumns, setHiddenColumns] = useState<string[]>(DEFAULT_PREFS.hidden_columns)
  const [showColumnMenu, setShowColumnMenu] = useState(false)

  // Load preferences from Supabase on mount
  const prefsLoaded = useRef(false)
  useEffect(() => {
    if (!prefsLoaded.current) {
      prefsLoaded.current = true
      loadPrefsFromSupabase().then(prefs => {
        setColumnOrder(prefs.column_order)
        setColumnWidths(prefs.column_widths)
        setSortCol(prefs.sort_column)
        setSortDir(prefs.sort_direction)
        setHiddenColumns(prefs.hidden_columns || [])
      })
    }
  }, [])

  // Schwab connection status + reconnect flow
  const [schwabConnected, setSchwabConnected] = useState<boolean | null>(null)
  const [schwabConnecting, setSchwabConnecting] = useState(false)
  useEffect(() => {
    invokeEdgeFunction<{ connected: boolean }>('schwab-auth-connect', { params: { action: 'status' } })
      .then(r => setSchwabConnected(!!r.connected))
      .catch(() => setSchwabConnected(null))
  }, [])

  const handleReconnectSchwab = async () => {
    setSchwabConnecting(true)
    try {
      const r = await invokeEdgeFunction<{ url?: string; error?: string }>('schwab-auth-connect', {
        params: { action: 'authorize-url' },
      })
      if (r.url) {
        // Send the user to Schwab; they'll be redirected back to /schwab/callback.
        window.location.href = r.url
      } else {
        alert(r.error || 'Could not start Schwab connection.')
        setSchwabConnecting(false)
      }
    } catch (e) {
      alert((e as Error).message || 'Could not start Schwab connection.')
      setSchwabConnecting(false)
    }
  }

  // Delete a watchlist row (UI replacement for editing the Supabase table by hand).
  const handleDeleteRow = async (id: string, ticker: string) => {
    if (!confirm(`Remove ${ticker} from the watchlist? This cannot be undone.`)) return
    // Optimistic removal
    setRows(prev => prev.filter(r => r.id !== id))
    const { error } = await supabase.from('watchlist').delete().eq('id', id)
    if (error) {
      console.error('Failed to delete watchlist row:', error)
      alert(`Failed to delete ${ticker}. Please try again.`)
      fetchRows()
    }
  }

  // Toggle a column's visibility and persist.
  const toggleColumnHidden = (colId: string) => {
    setHiddenColumns(prev => {
      const next = prev.includes(colId) ? prev.filter(c => c !== colId) : [...prev, colId]
      savePrefsToSupabase({ hidden_columns: next })
      return next
    })
  }

  // Drag state for column reorder
  const [dragCol, setDragCol] = useState<string | null>(null)
  const [dragOverCol, setDragOverCol] = useState<string | null>(null)

  // Resize state
  const resizeRef = useRef<{ col: string; startX: number; startWidth: number } | null>(null)

  // Earnings data loaded from watchlist rows directly (earnings_date column)
  const earnings = useMemo(() => {
    const map: Record<string, { date: string; hour: string }> = {}
    for (const row of rows) {
      if (row.earnings_date) {
        map[row.ticker] = { date: row.earnings_date, hour: row.earnings_hour || '' }
      }
    }
    return map
  }, [rows])

  // Price state (manual refresh only — no auto-fetch or polling)
  const prices = useAppStore(s => s.prices)
  const fetchPrices = useAppStore(s => s.fetchPrices)
  const priceLastRefreshedAt = useAppStore(s => s.priceLastRefreshedAt)
  const earningsData = useAppStore(s => s.earningsData)
  const [refreshing, setRefreshing] = useState(false)
  const [refreshProgress, setRefreshProgress] = useState({ phase: '', done: 0, total: 0 })

  // ========== Data Loading ==========

  const fetchRows = useCallback(async () => {
    setLoading(true)
    const { data, error } = await supabase.from('watchlist').select('*').order('category').order('row_number')
    if (error) { console.error('Failed to fetch watchlist:', error); setLoading(false); return }
    setRows(data || [])
    setLoading(false)
  }, [])

  useEffect(() => { fetchRows() }, [fetchRows])

  // How many tickers the manual refresh processes per click. Finnhub's free tier is
  // 60 calls/min and each ticker's valuation = 1 call at ~1400ms pacing, so a full
  // 131-ticker refresh would rate-limit. We refresh the STALEST N per click; the daily
  // cron cycles the rest. Click again to top up the next batch.
  const MANUAL_REFRESH_BATCH = 40

  // Manual price refresh (includes earnings) — writes to Supabase for persistence.
  // Processes the STALEST MANUAL_REFRESH_BATCH tickers (oldest/never fundamentals_updated_at).
  const handleRefreshPrices = useCallback(async () => {
    // Stalest first: rows with no fundamentals_updated_at, then oldest timestamp.
    const staleTime = (r: any) => (r.fundamentals_updated_at ? new Date(r.fundamentals_updated_at).getTime() : 0)
    const batchRows = [...rows]
      .filter(r => r.ticker)
      .sort((a, b) => staleTime(a) - staleTime(b))
      .slice(0, MANUAL_REFRESH_BATCH)
    const tickers = batchRows.map(r => r.ticker)
    if (tickers.length === 0) return
    setRefreshing(true)
    setRefreshProgress({ phase: `Refreshing ${tickers.length} stalest`, done: 0, total: tickers.length })
    try {
      // Send this batch (Schwab handles them in one call; Finnhub valuation paced server-side)
      await fetchPrices(tickers, true, true) // includeHistory=true, includeEarnings=true
      setRefreshProgress({ phase: 'Saving to DB', done: 0, total: tickers.length })

      // Persist prices + computed columns to Supabase watchlist table (source of truth)
      const currentPrices = useAppStore.getState().prices
      const now = new Date().toISOString()
      let saved = 0

      for (const row of batchRows) {
        const priceData = currentPrices[row.ticker]
        if (!priceData || !priceData.currentPrice) continue

        const currentPrice = priceData.currentPrice
        const high52 = priceData.fiftyTwoWeekHigh || Number(row.high_52w) || 0

        // Compute derived columns if SMA data exists in the row
        const sma50 = row.sma_50 ? Number(row.sma_50) : null
        const sma20 = row.sma_20 ? Number(row.sma_20) : null
        const sma200 = row.sma_200 ? Number(row.sma_200) : null
        const techStatus = sma50 ? computeTechnicalStatus(currentPrice, sma50) : null
        const entryZone = sma50 ? computeIdealEntryZone(currentPrice, sma20, sma50, sma200) : null
        const discount = computeCompression(currentPrice, high52)

        await supabase.from('watchlist').update({
          price_current: currentPrice,
          high_52w: priceData.fiftyTwoWeekHigh || row.high_52w,
          high_6m: priceData.sixMonthHigh || row.high_6m,
          discount_6m: discount,
          ...(techStatus ? { technical_status: techStatus } : {}),
          ...(entryZone ? { ideal_entry_zone: entryZone } : {}),
          // Valuation metrics from Finnhub (manual-button parity with the daily cron)
          ...(priceData.forwardPe != null ? { forward_pe: priceData.forwardPe } : {}),
          ...(priceData.pegRatio != null ? { peg_ratio: priceData.pegRatio } : {}),
          ...(priceData.priceSales != null ? { price_sales: priceData.priceSales } : {}),
          ...(priceData.forwardPe != null || priceData.pegRatio != null || priceData.priceSales != null
            ? { fundamentals_updated_at: now }
            : {}),
          price_updated_at: now,
        }).eq('id', row.id)

        saved++
        setRefreshProgress({ phase: 'Saving to DB', done: saved, total: batchRows.length })
      }

      // Persist earnings data from edge function response to watchlist rows
      setRefreshProgress({ phase: 'Saving earnings', done: 0, total: 0 })
      const currentEarnings = useAppStore.getState().earningsData
      if (Object.keys(currentEarnings).length > 0) {
        const earningsEntries = Object.entries(currentEarnings).filter(([, info]) => info.date)
        for (const [ticker, info] of earningsEntries) {
          await supabase.from('watchlist').update({
            earnings_date: info.date,
            earnings_hour: info.timing || '',
          }).eq('ticker', ticker)
        }
        // Re-fetch rows to show badges immediately
        fetchRows()
      }

      setLastUpdated(new Date())
      // Re-load rows so the freshly-written valuation columns (forward_pe/peg/ps) show now.
      await fetchRows()
    } finally {
      setRefreshing(false)
    }
  }, [rows, fetchPrices, fetchRows])

  // Compute staleness from DB timestamps (source of truth)
  const latestPriceUpdate = useMemo(() => {
    // Find the most recent price_updated_at across all rows
    let latest: string | null = null
    for (const row of rows) {
      if (row.price_updated_at && (!latest || row.price_updated_at > latest)) {
        latest = row.price_updated_at
      }
    }
    return latest
  }, [rows])

  const latestSmaUpdate = useMemo(() => {
    let latest: string | null = null
    for (const row of rows) {
      if (row.sma_updated_at && (!latest || row.sma_updated_at > latest)) {
        latest = row.sma_updated_at
      }
    }
    return latest
  }, [rows])

  // Use DB timestamp (more reliable) or fall back to Zustand state
  const effectiveRefreshTime = latestPriceUpdate || priceLastRefreshedAt
  const cacheAgeMinutes = effectiveRefreshTime
    ? Math.floor((Date.now() - new Date(effectiveRefreshTime).getTime()) / 60_000)
    : Infinity
  const isStale = cacheAgeMinutes > 360 // 6 hours
  const cacheAgeLabel = cacheAgeMinutes === Infinity ? 'Never refreshed'
    : cacheAgeMinutes < 60 ? `${cacheAgeMinutes}m ago`
    : `${Math.round(cacheAgeMinutes / 60)}h ago`

  const smaAgeMinutes = latestSmaUpdate
    ? Math.floor((Date.now() - new Date(latestSmaUpdate).getTime()) / 60_000)
    : Infinity
  const smaAgeLabel = smaAgeMinutes === Infinity ? 'Never'
    : smaAgeMinutes < 60 ? `${smaAgeMinutes}m ago`
    : `${Math.round(smaAgeMinutes / 60)}h ago`
  const isSmaStale = smaAgeMinutes > 1440 // 24 hours

  // ========== Computed Values ==========

  const enrichedRows = useMemo(() => {
    return rows.map(row => {
      const priceData = prices[row.ticker]
      const sma = smaData[row.ticker]
      // Only use live price if available and non-zero; otherwise keep DB value
      const currentPrice = (priceData?.currentPrice && priceData.currentPrice > 0) ? priceData.currentPrice : (Number(row.price_current) || 0)
      // Only override 52W high if we have a fresh value from API
      const high52 = (priceData?.fiftyTwoWeekHigh && priceData.fiftyTwoWeekHigh > 0) ? priceData.fiftyTwoWeekHigh : (Number(row.high_52w) || 0)
      // Compute discount from whichever high we have
      const compression = computeCompression(currentPrice, high52)
      // Tech status / entry zone: use fresh SMA from state, OR DB-persisted SMA columns as fallback
      // IMPORTANT: Supabase returns NUMERIC columns as strings — must parse to number
      const sma50 = sma?.sma_50 || (row.sma_50 ? Number(row.sma_50) : null)
      const sma20 = sma?.sma_20 || (row.sma_20 ? Number(row.sma_20) : null)
      const sma200 = sma?.sma_200 || (row.sma_200 ? Number(row.sma_200) : null)
      const techStatus = sma50 ? computeTechnicalStatus(currentPrice, sma50) : row.technical_status
      const entryZone = sma50 ? computeIdealEntryZone(currentPrice, sma20, sma50, sma200) : row.ideal_entry_zone

      return {
        ...row,
        price_current: currentPrice,
        high_52w: high52,
        discount_6m: compression ?? row.discount_6m,
        technical_status: techStatus,
        ideal_entry_zone: entryZone,
      }
    })
  }, [rows, prices, smaData])

  // ========== Sorting ==========

  const sortedRows = useMemo(() => {
    let filtered = enrichedRows
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      filtered = enrichedRows.filter(r =>
        r.ticker?.toLowerCase().includes(q) || r.company_name?.toLowerCase().includes(q) || r.category?.toLowerCase().includes(q)
      )
    }
    return [...filtered].sort((a, b) => {
      let va = a[sortCol]
      let vb = b[sortCol]
      if (va == null) return 1
      if (vb == null) return -1

      // Custom sort orders for specific columns
      if (sortCol === 'technical_status') {
        const TECH_ORDER: Record<string, number> = { 'Pullback': 1, 'Support': 2, 'Neutral': 3, 'Breakout': 4, 'Extended': 5 }
        const oa = TECH_ORDER[va] || 99
        const ob = TECH_ORDER[vb] || 99
        return sortDir === 'asc' ? oa - ob : ob - oa
      }

      if (sortCol === 'ideal_entry_zone') {
        // Extract SMA number from strings like "20 SMA ($418.61)" or "200 SMA ($15.24)"
        const getSmaNum = (v: string): number => {
          const match = v.match(/^(\d+)\s*SMA/)
          return match ? parseInt(match[1]) : 999
        }
        const oa = getSmaNum(String(va))
        const ob = getSmaNum(String(vb))
        return sortDir === 'asc' ? oa - ob : ob - oa
      }

      if (typeof va === 'string') va = va.toLowerCase()
      if (typeof vb === 'string') vb = vb.toLowerCase()
      if (va < vb) return sortDir === 'asc' ? -1 : 1
      if (va > vb) return sortDir === 'asc' ? 1 : -1
      return 0
    })
  }, [enrichedRows, sortCol, sortDir, searchQuery])

  // Group by category
  const grouped = useMemo(() => {
    const map: Record<string, any[]> = {}
    for (const row of sortedRows) {
      const cat = row.category || 'Other'
      if (!map[cat]) map[cat] = []
      map[cat].push(row)
    }
    return map
  }, [sortedRows])

  // ========== Handlers ==========

  const handleSort = (colId: string) => {
    const newDir = sortCol === colId ? (sortDir === 'asc' ? 'desc' : 'asc') : 'desc'
    setSortCol(colId)
    setSortDir(newDir)
    savePrefsToSupabase({ sort_column: colId, sort_direction: newDir })
  }

  // Column drag-to-reorder
  const handleDragStart = (colId: string) => setDragCol(colId)
  const handleDragOver = (e: React.DragEvent, colId: string) => { e.preventDefault(); setDragOverCol(colId) }
  const handleDrop = (targetCol: string) => {
    if (!dragCol || dragCol === targetCol) { setDragCol(null); setDragOverCol(null); return }
    const newOrder = [...columnOrder]
    const fromIdx = newOrder.indexOf(dragCol)
    const toIdx = newOrder.indexOf(targetCol)
    newOrder.splice(fromIdx, 1)
    newOrder.splice(toIdx, 0, dragCol)
    setColumnOrder(newOrder)
    savePrefsToSupabase({ column_order: newOrder })
    setDragCol(null)
    setDragOverCol(null)
  }

  // Column resize
  const handleResizeStart = (e: React.MouseEvent, colId: string) => {
    e.preventDefault()
    e.stopPropagation()
    resizeRef.current = { col: colId, startX: e.clientX, startWidth: columnWidths[colId] || 100 }
    const onMove = (ev: MouseEvent) => {
      if (!resizeRef.current) return
      const diff = ev.clientX - resizeRef.current.startX
      const newWidth = Math.max(40, resizeRef.current.startWidth + diff)
      setColumnWidths(prev => {
        const updated = { ...prev, [resizeRef.current!.col]: newWidth }
        return updated
      })
    }
    const onUp = () => {
      resizeRef.current = null
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      // Save final widths to Supabase (once on mouseup, not every pixel)
      setColumnWidths(prev => { savePrefsToSupabase({ column_widths: prev }); return prev })
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  // Inline edit
  const handleCellEdit = async (rowId: string, field: string, value: string) => {
    // Optimistic
    setRows(prev => prev.map(r => r.id === rowId ? { ...r, [field]: value } : r))
    const { error } = await supabase.from('watchlist').update({ [field]: value }).eq('id', rowId)
    if (error) { console.error('Save failed:', error); fetchRows() }
  }

  // ========== Render ==========

  // Columns that can never be hidden (keep the table anchored).
  const NON_HIDEABLE = new Set(['row_number', 'ticker'])
  const hiddenSet = new Set(hiddenColumns)
  const colDefs = columnOrder
    .map(id => ALL_COLUMNS.find(c => c.id === id)!)
    .filter(Boolean)
    .filter(c => !hiddenSet.has(c.id))

  function formatCell(row: any, colId: string): string {
    const val = row[colId]
    if (val == null || val === '') return '—'
    if (colId === 'price_current' || colId === 'high_52w' || colId === 'high_6m') {
      return `$${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    }
    if (colId === 'discount_6m') return `${Number(val).toFixed(1)}%`
    if (colId === 'forward_pe' || colId === 'peg_ratio' || colId === 'price_sales') {
      return Number(val).toFixed(2)
    }
    return String(val)
  }

  function getRowBg(row: any): string | undefined {
    if (row.technical_status === 'Extended') return 'rgba(239, 68, 68, 0.1)'
    if (row.technical_status === 'Pullback' && row.volatility === 'High') return 'rgba(34, 197, 94, 0.15)'
    return undefined
  }

  if (loading) return <div className="p-8 text-center text-gray-500">Loading watchlist...</div>

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-200 px-4 pt-3 pb-2">
        {/* Stale data banner */}
        {isStale && rows.length > 0 && (
          <div className="mb-2 px-3 py-2 rounded bg-amber-50 border border-amber-200 text-amber-800 text-xs flex items-center justify-between">
            <span>⚠️ Prices are stale ({cacheAgeLabel}). Click Refresh to update.</span>
            <button
              onClick={handleRefreshPrices}
              disabled={refreshing}
              className="px-2 py-0.5 rounded bg-amber-600 text-white text-[10px] hover:bg-amber-500 disabled:opacity-50"
            >
              {refreshing ? 'Refreshing...' : 'Refresh Now'}
            </button>
          </div>
        )}
        {rows.filter(r => !r.technical_status).length > 0 && !smaLoading && (
          <div className="mb-2 px-3 py-2 rounded bg-orange-50 border border-orange-200 text-orange-800 text-xs">
            ⚠️ {rows.filter(r => !r.technical_status).length} tickers missing SMA data (Tech Status / Entry Zone). Click "↻ SMAs" to fetch.
          </div>
        )}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <h1 className="text-lg font-bold text-gray-900">Watchlist</h1>
            {effectiveRefreshTime && (
              <span className={`text-[11px] px-2 py-0.5 rounded font-medium ${isStale ? 'bg-red-100 text-red-700' : cacheAgeMinutes > 240 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                {isStale ? '🔴' : cacheAgeMinutes > 240 ? '🟡' : '🟢'} Prices: {cacheAgeLabel}
              </span>
            )}
            {!effectiveRefreshTime && (
              <span className="text-[11px] bg-red-100 text-red-700 px-2 py-0.5 rounded font-medium">🔴 No prices loaded</span>
            )}
            {latestSmaUpdate && (
              <span className={`text-[11px] px-2 py-0.5 rounded font-medium ${isSmaStale ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                {isSmaStale ? '🔴' : '🟢'} SMAs: {smaAgeLabel}
              </span>
            )}
            {!latestSmaUpdate && !smaLoading && rows.filter(r => !r.technical_status).length > 0 && (
              <span className="text-[11px] bg-red-100 text-red-700 px-2 py-0.5 rounded font-medium">🔴 SMAs: {rows.filter(r => !r.technical_status).length} missing</span>
            )}
            {!latestSmaUpdate && !smaLoading && rows.filter(r => !r.technical_status).length === 0 && rows.length > 0 && (
              <span className="text-[11px] bg-green-100 text-green-700 px-2 py-0.5 rounded font-medium">🟢 SMAs: Complete</span>
            )}
            {smaLoading && <span className="text-[10px] text-blue-600 animate-pulse">Loading SMAs ({smaProgress.done}/{smaProgress.total})...</span>}
          </div>
          <div className="flex items-center gap-2">
            <button
              className={`px-3 py-1.5 text-[11px] rounded font-medium border ${refreshing ? 'bg-gray-100 text-gray-400 border-gray-200' : 'bg-blue-600 text-white hover:bg-blue-500 border-blue-600'}`}
              onClick={handleRefreshPrices}
              disabled={refreshing}
            >
              {refreshing ? `⏳ ${refreshProgress.phase}${refreshProgress.total > 0 ? ` (${refreshProgress.done}/${refreshProgress.total})` : '...'}` : '🔄 Refresh Prices'}
            </button>
            <button
              className="px-2.5 py-1.5 text-[11px] rounded bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
              onClick={() => setSmaConfirm(true)}
              disabled={smaLoading}
            >{smaLoading ? `↻ ${smaProgress.done}/${smaProgress.total}` : `↻ SMAs${isSmaStale ? ' (stale)' : ''}`}</button>
            <div className="relative">
              <button
                className="px-2.5 py-1.5 text-[11px] rounded font-medium bg-indigo-600 text-white hover:bg-indigo-500 border border-indigo-600"
                onClick={() => setShowColumnMenu(v => !v)}
                title="Show or hide watchlist columns"
              >⚙ Columns</button>
              {showColumnMenu && (
                <>
                  {/* click-away backdrop */}
                  <div className="fixed inset-0 z-30" onClick={() => setShowColumnMenu(false)} />
                  <div className="absolute right-0 mt-1 z-40 w-56 max-h-80 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-xl p-2">
                    <p className="px-2 py-1 text-[10px] font-semibold uppercase text-gray-400">Show / hide columns</p>
                    {ALL_COLUMNS.map(col => {
                      const locked = NON_HIDEABLE.has(col.id)
                      const visible = !hiddenSet.has(col.id)
                      return (
                        <label
                          key={col.id}
                          className={`flex items-center gap-2 px-2 py-1 text-xs rounded ${locked ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100 cursor-pointer'}`}
                        >
                          <input
                            type="checkbox"
                            checked={visible}
                            disabled={locked}
                            onChange={() => !locked && toggleColumnHidden(col.id)}
                          />
                          <span>{col.label}</span>
                        </label>
                      )
                    })}
                  </div>
                </>
              )}
            </div>
            <button
              className={`px-2.5 py-1.5 text-[11px] rounded font-medium border ${
                schwabConnected === false
                  ? 'bg-amber-500 text-white hover:bg-amber-400 border-amber-500'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300'
              }`}
              onClick={handleReconnectSchwab}
              disabled={schwabConnecting}
              title={schwabConnected === false ? 'Schwab is disconnected — click to reconnect' : 'Reconnect your Schwab account'}
            >
              {schwabConnecting
                ? 'Connecting...'
                : schwabConnected === false
                  ? '⚠️ Reconnect Schwab'
                  : schwabConnected
                    ? '🔗 Schwab'
                    : '🔗 Schwab'}
            </button>
            <button
              className="px-2.5 py-1.5 text-[11px] rounded bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
              onClick={() => setShowAddModal(true)}
            >+ Add Stock</button>
          </div>
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search ticker, name, or category..."
          className="w-full px-3 py-1.5 rounded bg-white border border-gray-300 text-gray-900 text-xs placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
        />
      </div>

      {/* Table — single table so columns align across all categories */}
      <div className="flex-1 overflow-auto">
        <table className="w-full text-[13px] border-collapse" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            {colDefs.map(col => (
              <col key={col.id} style={{ width: columnWidths[col.id] || col.width }} />
            ))}
            <col style={{ width: 44 }} />{/* actions (delete) */}
          </colgroup>
          <thead className="sticky top-0 z-10">
            <tr className="bg-gray-50">
              {colDefs.map(col => (
                <th
                  key={col.id}
                  className={`px-1.5 py-1.5 text-left font-semibold text-gray-500 uppercase whitespace-nowrap cursor-pointer select-none border-b border-gray-200 relative ${dragOverCol === col.id ? 'border-l-2 border-l-blue-500' : ''}`}
                  draggable
                  onDragStart={() => handleDragStart(col.id)}
                  onDragOver={(e) => handleDragOver(e, col.id)}
                  onDrop={() => handleDrop(col.id)}
                  onDragEnd={() => { setDragCol(null); setDragOverCol(null) }}
                  onClick={() => handleSort(col.id)}
                >
                  <span>{col.label}</span>
                  {VALUATION_COLUMNS.has(col.id) && (
                    <span onClick={(e) => e.stopPropagation()}>
                      <Tooltip content={VALUATION_TOOLTIPS[col.id as 'forward_pe' | 'peg_ratio' | 'price_sales']} />
                    </span>
                  )}
                  {sortCol === col.id && <span className="ml-0.5 text-blue-600">{sortDir === 'asc' ? '↑' : '↓'}</span>}
                  <div
                    className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400/50"
                    onMouseDown={(e) => handleResizeStart(e, col.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </th>
              ))}
              <th className="px-1.5 py-1.5 border-b border-gray-200" aria-label="Actions"></th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(grouped).map(([category, catRows]) => (
              <>
                {/* Category separator row */}
                <tr key={`cat-${category}`} className="bg-gray-50">
                  <td colSpan={colDefs.length + 1} className="px-3 py-2 text-sm font-semibold text-gray-600 border-b border-gray-200">
                    ▼ {category} ({catRows.length})
                  </td>
                </tr>
                {/* Data rows */}
                {catRows.map(row => (
                  <tr
                    key={row.id}
                    className="group border-b border-gray-100 hover:bg-gray-50 transition-colors"
                    style={getRowBg(row) ? { backgroundColor: getRowBg(row) } : undefined}
                  >
                    {colDefs.map(col => (
                      <td
                        key={col.id}
                        className="px-1.5 py-1.5 text-gray-700"
                        title={String(row[col.id] ?? '')}
                      >
                        {col.id === 'discount_6m' && row.discount_6m != null ? (
                          <span className={row.discount_6m > 25 ? 'text-green-600 font-semibold' : row.discount_6m > 15 ? 'text-orange-600 font-medium' : 'text-gray-600'}>
                            {formatCell(row, col.id)}
                          </span>
                        ) : VALUATION_COLUMNS.has(col.id) ? (
                          row[col.id] != null && row[col.id] !== '' ? (
                            <span className={`font-mono ${valuationColorClass(col.id, Number(row[col.id]))}`}>
                              {formatCell(row, col.id)}
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )
                        ) : col.id === 'technical_status' ? (
                          <TechBadge status={row.technical_status} />
                        ) : col.id === 'ticker' ? (
                          <span className="font-bold text-red-600 flex items-center gap-1">
                            {row.ticker}
                            <EarningsBadge ticker={row.ticker} earnings={earnings} />
                          </span>
                        ) : col.id === 'price_current' ? (
                          <span className="font-mono text-gray-900">{formatCell(row, col.id)}</span>
                        ) : EDITABLE_COLUMNS.has(col.id) ? (
                          <EditableCell
                            value={row[col.id] ?? ''}
                            onSave={(val) => handleCellEdit(row.id, col.id, val)}
                          />
                        ) : (
                          formatCell(row, col.id)
                        )}
                      </td>
                    ))}
                    <td className="px-1.5 py-1.5 text-center">
                      <button
                        onClick={() => handleDeleteRow(row.id, row.ticker)}
                        className="text-gray-300 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100"
                        title={`Remove ${row.ticker} from watchlist`}
                        aria-label={`Remove ${row.ticker}`}
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </>
            ))}
          </tbody>
        </table>
      </div>

      {showAddModal && <AddStockModal onClose={() => { setShowAddModal(false); fetchRows() }} />}

      {/* SMA Refresh Confirmation Dialog */}
      {smaConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-lg shadow-xl p-5 max-w-sm w-full mx-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Refresh SMAs</h3>
            <p className="text-xs text-gray-600 mb-1">
              Total tickers: <strong>{rows.length}</strong> | Missing SMAs: <strong>{rows.filter(r => !r.technical_status).length}</strong>
            </p>
            <p className="text-xs text-gray-500 mb-4">
              Note: Twelve Data rate limit = ~8 tickers/min. Full refresh takes ~{Math.ceil(rows.length / 8)} min.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => startSmaFetch('all')}
                className="flex-1 px-3 py-2 text-xs rounded bg-blue-600 text-white hover:bg-blue-500 font-medium"
              >
                Refresh All ({rows.length})
              </button>
              <button
                onClick={() => startSmaFetch('missing')}
                className="flex-1 px-3 py-2 text-xs rounded bg-amber-600 text-white hover:bg-amber-500 font-medium"
                disabled={rows.filter(r => !r.technical_status).length === 0}
              >
                Missing Only ({rows.filter(r => !r.technical_status).length})
              </button>
              <button
                onClick={() => setSmaConfirm(false)}
                className="px-3 py-2 text-xs rounded bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ========== Badge Components ==========

function TechBadge({ status }: { status: string | null }) {
  if (!status || status === '—') return <span className="text-gray-400">—</span>
  const colors: Record<string, string> = {
    Extended: 'bg-red-100 text-red-700',
    Breakout: 'bg-orange-100 text-orange-700',
    Neutral: 'bg-gray-100 text-gray-600',
    Support: 'bg-blue-100 text-blue-700',
    Pullback: 'bg-green-100 text-green-700',
  }
  return <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${colors[status] || 'text-gray-400'}`}>{status}</span>
}

function EarningsBadge({ ticker, earnings }: { ticker: string; earnings: Record<string, { date: string; hour: string }> }) {
  const entry = earnings[ticker]
  if (!entry || !entry.date) return null

  const today = new Date()
  const earningsDate = new Date(entry.date + 'T00:00:00')
  const daysUntil = Math.ceil((earningsDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))

  // Only show if earnings are in the future and within 60 days
  if (daysUntil < 0 || daysUntil > 60) return null

  const color = daysUntil <= 7 ? 'bg-red-100 text-red-700' : daysUntil <= 14 ? 'bg-orange-100 text-orange-700' : daysUntil <= 30 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'
  const timing = entry.hour === 'bmo' ? '🌅' : entry.hour === 'amc' ? '🌇' : ''

  return (
    <span className={`px-1 py-0.5 rounded text-[9px] font-medium ${color}`} title={`Earnings: ${entry.date} (${entry.hour === 'bmo' ? 'Before Open' : entry.hour === 'amc' ? 'After Close' : 'TBD'})`}>
      📅{daysUntil}d{timing}
    </span>
  )
}


function EditableCell({ value, onSave }: { value: string; onSave: (val: string) => void }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => { setDraft(value) }, [value])
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus() }, [editing])

  if (!editing) {
    return (
      <span
        className="cursor-pointer hover:bg-blue-50 px-0.5 rounded min-w-[20px] inline-block"
        onDoubleClick={() => setEditing(true)}
        title="Double-click to edit"
      >
        {value || '—'}
      </span>
    )
  }

  return (
    <input
      ref={inputRef}
      className="w-full bg-white border border-blue-400 rounded px-1 py-0.5 text-[13px] text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500"
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={() => { setEditing(false); if (draft !== value) onSave(draft) }}
      onKeyDown={e => {
        if (e.key === 'Enter') { setEditing(false); if (draft !== value) onSave(draft) }
        if (e.key === 'Escape') { setEditing(false); setDraft(value) }
      }}
    />
  )
}
